import React, { useState } from "react";

import { Typography} from '../../components';

import {
  LayoutContainer,
  TopRow,
  SliderBox,
  TableBox,
  ChartsRow,
  ChartBox,
  Container,
  Introduction,
  IntroductionText
} from "./styled";
import "./styles.css";

import { Slider, SliderThumb } from "@mui/material";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// --- Custom slider thumb
function SliderThumbComponent(props) {
  return <SliderThumb {...props} />;
}

export default function KostenPage() {
  // --- SLIDERS ---
  const [avgCouples, setAvgCouples] = useState(5); // 2-6
  const [numSeries, setNumSeries] = useState(6); // 1-30
  const [subsidy, setSubsidy] = useState(100); // 0 of 100
  const [overhead, setOverhead] = useState(50); // 0-100

  // --- CONSTANTS ---
  const hoursPerReeks = 6;
  const opstartkosten = 2500;
  const abonnement = 600;
  const kostenPerKoppel = 40;
  const opbrengstPerKoppel = 172;

  const round1 = (value) => Math.round(value * 10) / 10;

  // --- HULPFUNCTIES ---
  const calcHoursOverhead = () => (3 + avgCouples) * (overhead / 100);
  const calcTotalHours = () => hoursPerReeks + calcHoursOverhead();

  // --- DATA JAARLIJKS ---
  const years = 10;
  const tableData = [];
  let cumulativeBalance = 0;
  let cumulativeTime = 0;

  const calcYearCosts = (year) => {
      const baseCosts =
      abonnement +
      kostenPerKoppel * avgCouples * numSeries;

      return year === 1 ? baseCosts + opstartkosten : baseCosts;
  };

  const calcTotalSubsidy = () => {
      if (subsidy === 0) return 0;

      const year1Costs = calcYearCosts(1);
      const year2Costs = calcYearCosts(2);

      return (year1Costs + year2Costs) * (subsidy / 100);
  };


  for (let year = 1; year <= years; year++) {
    const costs =
      abonnement +
      (kostenPerKoppel * avgCouples * numSeries) +
      (year === 1 ? opstartkosten : 0);
    const revenues =
      (year === 1 ? 0.5 : 1) * opbrengstPerKoppel * avgCouples * numSeries;
    const yearlySubsidy = year <= 2 ? subsidy : 0;
    const balance = revenues - costs + (costs * yearlySubsidy / 100);

    cumulativeBalance += balance;
    const timeInvest = numSeries * calcTotalHours();
    cumulativeTime += timeInvest;

    tableData.push({
      year,
      costs,
      revenues,
      subsidy: (costs * yearlySubsidy / 100),
      balance,
      cumulativeBalance,
      timeInvest,
      cumulativeTime,
    });
  }

  // --- GRAFIEK DATA ---
  const barData = {
    labels: tableData.map((row) => `Jaar ${row.year}`),
    datasets: [
      { label: "Kosten", data: tableData.map((r) => r.costs), backgroundColor: "#e74c3c" },
      { label: "Opbrengsten", data: tableData.map((r) => r.revenues), backgroundColor: "#2ecc71" },
      { label: "Subsidie", data: tableData.map((r) => r.subsidy), backgroundColor: "#3498db" },
      { label: "Cumulatief", data: tableData.map((r) => r.cumulativeBalance), backgroundColor: "#f1c40f" },
    ],
  };

  const lineData = {
    labels: tableData.map((row) => `Jaar ${row.year}`),
    datasets: [
      {
        label: "Uurtarief jaar",
        data: tableData.map((r) => (r.revenues - r.costs + r.subsidy) / r.timeInvest),
        borderColor: "#8e44ad",
        backgroundColor: "#8e44ad",
        tension: 0.3,
      },
      {
        label: "Cumulatief uurtarief",
        data: tableData.map((r) => r.cumulativeBalance / r.cumulativeTime),
        borderColor: "#e67e22",
        backgroundColor: "#e67e22",
        tension: 0.3,
      },
    ],
  };

  return (
      <div className="KostenPage">
      <Container>
          <Introduction>
              <IntroductionText>
                    <div>
                        <Typography type='h1' style={{ color: '#032d34' }}>Financieel rekenmodel</Typography>
                    </div>
                        <Typography type='h2' style={{ color: '#145f68', fontSize: '1.3em' }}>ontdek de kosten en opbrengsten<br />van NOLIA BabyFeeding</Typography><br/>
                        <Typography type='paragraph' style={{ fontSize: '1.1em' }}>
                            Met dit rekenmodel krijg je inzicht in de financiële kant van het implementeren van NOLIA BabyFeeding. 
                            Pas de variabelen aan en ontdek wat de impact is op de kosten, opbrengsten, balans en tijdsinvestering.<br /><br />
                            Laat de tabel en grafieken ondersteunend zijn aan jouw besluitvorming.
                            <br /><br />
                            <b>Let op:</b> Deze tool moet nog worden aangepast aan de vernieuwde kostenstructuur van NOLIA BabyFeeding. WIl je een update ontvangen zodra dit is gebeurd? Stuur dan een mail naar <a href="mailto:info@babyfeeding.nl">info@babyfeeding.nl</a>
                        </Typography>
              </IntroductionText>
                    
            </Introduction>
      </Container>
      <LayoutContainer>
        {/* --- LINKS: Sliders + tabel --- */}
        <TopRow>
          <SliderBox>
            <h2>Stel hier jouw waarden in</h2>
            <div className="SliderGroup">
              <label>Gemiddeld aantal koppels per reeks: {avgCouples}</label>
              <Slider
                min={2}
                max={6}
                step={1}
                value={avgCouples}
                onChange={(e, v) => setAvgCouples(v)}
                components={{ Thumb: SliderThumbComponent }}
              />

              <label>Aantal reeksen per jaar: {numSeries}</label>
              <Slider
                min={1}
                max={30}
                step={1}
                value={numSeries}
                onChange={(e, v) => setNumSeries(v)}
                components={{ Thumb: SliderThumbComponent }}
              />

              <label>Subsidie: {subsidy}% (= {calcTotalSubsidy()} euro)</label>
              <Slider
                min={0}
                max={100}
                step={10}
                value={subsidy}
                onChange={(e, v) => setSubsidy(v)}
                components={{ Thumb: SliderThumbComponent }}
              />

              <label>Aandeel overhead: {overhead}% (= {calcHoursOverhead()} uur per reeks)</label>
              <Slider
                min={0}
                max={100}
                step={10}
                value={overhead}
                onChange={(e, v) => setOverhead(v)}
                components={{ Thumb: SliderThumbComponent }}
              />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <Typography type='paragraph' style={{ fontSize: '0.9em', color: '#555' }}>
                Toelichting: Pas de sliders aan en ontdek direct de impact op de kosten, opbrengsten, balans en tijdsinvestering. 
                Gebruik deze inzichten om een weloverwogen beslissing te nemen over het implementeren van NOLIA BabyFeeding in jouw organisatie.
              </Typography>
            </div>
          </SliderBox>

          <TableBox>
            <h2>Overzicht kosten - baten (per boekjaar)</h2>
              <table>
                <thead>
                  <tr>
                    <th>Jaar</th>
                    <th>Kosten</th>
                    <th>Opbrengsten</th>
                    <th>Subsidie</th>
                    <th>Balans</th>
                    <th>Cumulatief</th>
                    <th>Tijdsinvestering</th>
                    <th>Cumulatief</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row) => (
                    <tr key={row.year}>
                      <td>{row.year}</td>
                      <td>€{round1(row.costs)}</td>
                      <td>€{row.revenues}</td>
                      <td>€{round1(row.subsidy)}</td>
                      <td>€{round1(row.balance)}</td>
                      <td>€{round1(row.cumulativeBalance)}</td>
                      <td>{round1(row.timeInvest)} uur</td>
                      <td>{round1(row.cumulativeTime)} uur</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </TableBox>
        </TopRow>

        {/* --- RECHTS: Grafieken --- */}
        <ChartsRow>
          <ChartBox>
            <h2 style={{ textAlign: 'center' }}>Balans</h2>
            <h3 style={{ textAlign: 'center' }}>Per boekjaar en cumulatief</h3>
            <Bar data={barData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
          </ChartBox>

          <ChartBox>
            <h2 style={{ textAlign: 'center' }}>Uurtarief</h2>
            <h3 style={{ textAlign: 'center' }}>Per jaar en cumulatief</h3>
            <Line data={lineData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
          </ChartBox>
        </ChartsRow>
      </LayoutContainer>

      <Container>
          <Introduction>
              <IntroductionText>
                        <Typography type='h2' style={{ color: '#145f68', fontSize: '1.3em' }}>Het 'geheim' van een succesvolle dienstverlening?</Typography><br/>
                        <br />
                        <Typography type='paragraph' style={{textAlign: 'left', fontSize: '1.1em' }}>
                            Een succesvolle implementatie van NOLIA BabyFeeding begint met slimme keuzes rondom organisatie en samenwerking.
                            Samen dragen deze factoren bij aan een gezonde financiële balans en maken ze het gemakkelijker om de dienstverlening efficiënt en duurzaam in te richten.<br /><br />
                            <b>Een sterk netwerk</b><br />
                            Door het aandeel overhead te beperken en te werken met een sterk lokaal zorgnetwerk worden ouders sneller doorverwezen en kan er meer tijd naar de kerntaak: het verlenen van zorg.<br /><br />
                            <b>Gemeentelijke subsidie</b><br />
                            Een gemeentelijke subsidie kan de opstart aanzienlijk vergemakkelijken, waardoor initiële kosten worden verlaagd en de eerste groepen soepeler van start kunnen gaan. Het effect is duidelijk zichtbaar in de grafiek 'Uurtarief'<br /><br />
                            <b>Samenwerking</b><br />
                            Door samen te werken met andere professionals en organisaties kun je de kosten delen, de kwaliteit verhogen en een breder bereik realiseren. 
                            Denk aan het delen van materialen en faciliteiten, het samen dragen van organisatorische werkzaamheden of de promotie onder (aanstaande) ouders.
                        </Typography>
                        <br /><br />
                        <Typography type='h2' style={{ color: '#145f68', fontSize: '1.3em' }}>Integrale en collegiale samenwerking:<br />meer impact en meer werkplezier</Typography><br/>
                        <br />
              </IntroductionText>
                    
            </Introduction>
      </Container>

    </div>
  );
}
